import { useEffect, useMemo, useRef } from 'react';
import { useDispatch, useSelector, shallowEqual } from 'react-redux';
import { AesUtil } from '../js/AesUtil';
import { DOOR_KEY_TYPES, LAUNCHED, ALLIANTS_CHAT, ENABLE_SOFT_PHONE } from '../config/Constant';
import { setDoorKeyType, setOpenChat, setServiceStatus } from '../Redux/Reducers/appConfigReducer';
import { initializeDoorKey } from '../Apps/doorKey/initializeDoorkey';
import { initializeChat, handleOpenChat } from '../Apps/chat/initializeChat';
import { fetchAdhoc } from '../Redux/Actions/adhocAction';
import { chatLogin } from '../Apps/chat/handleChatState';
import { setAlliantsAndroidFcmToken } from '../Apps/chat/alliantsChat';
import initializeSoftphone from '../Apps/Softphone/initializeSoftpone';
import { toBool } from '../utils/Helper';
import { setIsSoftphoneEnabled } from '../Redux/Reducers/softphoneReducer';


/**
 * Custom hook to manage various app initializations
 * Includes: door keys, chat, FCM tokens, AES encryption
 */
export const useInitializations = () => {
    const dispatch = useDispatch();
    const renderAppTimeout = useRef(null);

    // Memoize AesUtil instance to avoid recreation on every render
    const aesUtil = useMemo(() => new AesUtil(128, 1000), []);

    const {
        webAppWidgetsInfo,
        fcmToken,
        isLoggedIn,
        preCheckInCompleted,
        isOnline,
        chatInterface,
        openChat,
        renderApp,
        appSessionToken,
        isSoftphoneEnabled,
        isBookingFound
    } = useSelector(
        state => ({
            webAppWidgetsInfo: state.deviceConfigSlice.webAppWidgetsInfo,
            fcmToken: state.appConfigSlice.fcmToken,
            isLoggedIn: state.appConfigSlice.isLoggedIn,
            preCheckInCompleted:  state.checkInSlice.preCheckInStatus?.preCheckInCompleted,
            isOnline: state.appConfigSlice.isOnline,
            chatInterface: state.appPreservedConfigSlice.chatInterface,
            openChat: state.appConfigSlice.openChat,
            renderApp: state.appConfigSlice.renderApp,
            appSessionToken: state.appConfigSlice.appSessionToken,
            isSoftphoneEnabled: state.softphoneSlice.isSoftphoneEnabled,
            isBookingFound: state.checkInSlice.bookingStatus.isBookingFound
        }),
        shallowEqual
    );

    /**
     * Initialize door key interface
     */
    const updateAndInitializeDoorkeyInterface = () => {
        const digitalKeyInterface = webAppWidgetsInfo.appConfigs?.digitalKeyInterface;

        if (digitalKeyInterface) {
            const doorKeyType = Object.keys(DOOR_KEY_TYPES).find((key) =>
                DOOR_KEY_TYPES[key].includes(digitalKeyInterface)
            );

            if (doorKeyType) {
                dispatch(setDoorKeyType(doorKeyType));
                dispatch(initializeDoorKey(doorKeyType));
            }
        }
    };

    // Initialize door key when widgets are loaded
    useEffect(() => {
        if (Object.keys(webAppWidgetsInfo).length > 0) {
            updateAndInitializeDoorkeyInterface();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [webAppWidgetsInfo, fcmToken, isLoggedIn]);



    // login for Alliants chat on iOS when the user is logged in
    useEffect(() => {
        if (isOnline && chatInterface?.interfaceKey && (isLoggedIn || isBookingFound)) {
            dispatch(chatLogin());
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isLoggedIn, isBookingFound, isOnline, chatInterface?.interfaceKey]);

    // Open chat when requested
    useEffect(() => {
        if (chatInterface?.interfaceKey && openChat) {
            dispatch(setOpenChat(false));
            dispatch(handleOpenChat());
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [chatInterface?.interfaceKey, openChat]);

    // Initialize chat when online and interface key is available
    useEffect(() => {
        if (isOnline && chatInterface.interfaceKey) {
            dispatch(initializeChat());
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isOnline, chatInterface.interfaceKey]);



    useEffect(() => {
        if (renderApp) {
            dispatch(fetchAdhoc());
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [renderApp, isBookingFound, preCheckInCompleted, isLoggedIn]);

    const canproceedInitSoftphone = () => {
        const isSoftphoneEnable = toBool(webAppWidgetsInfo.appConfigs?.enableSoftphone) || ENABLE_SOFT_PHONE;
        if(isSoftphoneEnable){
            dispatch(setIsSoftphoneEnabled(true));
            dispatch(initializeSoftphone());
        }
    }

    useEffect(() => {
        // added timeout as we cache webAppWidgetsInfo but constant file isn't loaded while time of execution.
        const timer = setTimeout(() => {
            if(isOnline && Object.keys(webAppWidgetsInfo).length > 0 && !isSoftphoneEnabled){
                canproceedInitSoftphone();
            }
        }, 1000);

        return () => clearTimeout(timer);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    },[ isOnline, webAppWidgetsInfo, isSoftphoneEnabled])

    
    // Set FCM token for Alliants anonymous mode push notifications on Android.
    // Runs whenever the token becomes available, even before authenticated login.
    useEffect(() => {
        if (chatInterface?.interfaceKey?.toLowerCase() === ALLIANTS_CHAT && fcmToken) {
            setAlliantsAndroidFcmToken(fcmToken);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [fcmToken, chatInterface?.interfaceKey]);

    return null;
};
